import { useEffect } from 'react';

/**
 * Lightweight SEO hook — sets document.title and the common meta tags on
 * every route change. Pass anything you have; the hook falls back to
 * site-wide defaults for anything missing.
 *
 *   useSeo({
 *     title:       'Ocean Shore Dining Table',
 *     description: 'Handcrafted live-edge...',
 *     image:       '/img/ocean-shore.jpg',
 *     keywords:    'resin dining table, live edge',
 *     type:        'product',
 *     canonical:   '/product/ocean-shore-dining-table',
 *   });
 *
 * There is no dependency on react-helmet — we mutate the document head
 * directly and revert on unmount so client-side navigation stays clean.
 */
const DEFAULTS = {
  brand:       'Maa Florist Mumbai',
  title:       'Maa Florist Mumbai | Handcrafted Epoxy Resin Furniture & Art',
  description: 'Handcrafted epoxy resin dining tables, coffee tables, wall clocks and art — handmade in India, delivered fully assembled.',
  image:       '',
  keywords:    'resin furniture, epoxy resin dining table, live edge, handmade furniture, resin wall art, resin wall clock, India',
  type:        'website',
};

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * Structured data (JSON-LD) helper. Pass a single JSON-LD object or an array;
 * we inject one <script type="application/ld+json"> per call and remove it
 * automatically when the effect re-runs.
 */
function injectJsonLd(payload) {
  if (!payload) return () => {};
  const s = document.createElement('script');
  s.type = 'application/ld+json';
  s.setAttribute('data-vrs-jsonld', '1');
  s.textContent = JSON.stringify(payload);
  document.head.appendChild(s);
  return () => { s.parentNode && s.parentNode.removeChild(s); };
}

export default function useSeo({ title, description, image, keywords, type, canonical, jsonLd } = {}) {
  useEffect(() => {
    const t = title       ? `${title} | ${DEFAULTS.brand}`.replace(new RegExp(` \\| ${DEFAULTS.brand}$`), (m) => title.includes(DEFAULTS.brand) ? '' : m) : DEFAULTS.title;
    const d = description || DEFAULTS.description;
    const k = keywords    || DEFAULTS.keywords;
    const i = image       || DEFAULTS.image;
    const tt= type        || DEFAULTS.type;
    const url = typeof window !== 'undefined' ? window.location.origin + (canonical || window.location.pathname) : '';

    document.title = t;
    upsertMeta('name', 'description', d);
    upsertMeta('name', 'keywords',    k);
    // Open Graph
    upsertMeta('property', 'og:title',       t);
    upsertMeta('property', 'og:description', d);
    upsertMeta('property', 'og:type',        tt);
    upsertMeta('property', 'og:image',       i);
    upsertMeta('property', 'og:url',         url);
    // Twitter
    upsertMeta('name', 'twitter:card',        i ? 'summary_large_image' : 'summary');
    upsertMeta('name', 'twitter:title',       t);
    upsertMeta('name', 'twitter:description', d);
    upsertMeta('name', 'twitter:image',       i);

    upsertLink('canonical', url);

    const removeLd = injectJsonLd(jsonLd);
    return () => { removeLd(); };
    // We serialize `jsonLd` to detect deep changes cheaply.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, image, keywords, type, canonical, JSON.stringify(jsonLd || null)]);
}
